///////////////
// IMPORTS
///////////////
// libraries
import * as React from "react";
import { makeRouteConfig, Route } from "found";
// components
import { ModernWrapperPage } from "@components/modern-wrapper";
import { HomeWrapper } from "@components/home-wrapper";
import { HomeUninstall, homeUninstallQuery } from "@components/home-uninstall";
import { NonUserDefragSurveyPage } from "@components/non-user-defrag-survey";
import { WebAppSlack, webAppSlackQuery } from "@components/web-app-slack";
import { LoginWrapperPage } from "@components/login-wrapper";
import { MicrosoftAuthRedirect } from "@components/microsoft-auth-redirect";
import { ChromeEventRescheduleWrapperPage } from "@components/chrome-event-reschedule-wrapper";
import { ChromeSlackWrapperPage } from "@components/chrome-slack-wrapper";
import { ChromeZoomVerifierPage } from "@components/chrome-zoom-verifier";
import { WebAppWrapperPage } from "@components/web-app-wrapper";
import { WebAppPage } from "@components/web-app";
import { WebAppCalendarRendererPage } from "@components/web-app-calendar";
import { SudoWrapperPage } from "@components/sudo-wrapper";
import { UnsubscribeWrapperPage } from "@components/unsubscribe-wrapper";
import { WebAppPricingPage } from "@components/web-app-pricing";
import { WebAppCheckoutPage } from "@components/web-app-checkout";
import { WebAppWorkingHoursPage } from "@components/web-app-working-hours";
import { WebAppTeamsPage } from "@components/web-app-teams";
import { WebSettings } from "@components/web-settings";
import { AdminPanelPage } from "@components/admin-panel";
import { AsanaWrapper, asanaWrapperQuery } from "@components/asana-wrapper";
import { AsanaAuth } from "@components/asana-auth";
import { AsanaTask } from "@components/asana-task";
import {
  SchedulingLinkRenderer,
  SetupSchedulingLinkRenderer,
  CancelBookingRenderer,
  RescheduleBookingRenderer,
} from "@components/scheduling-link";
import { LinksWelcome } from "@components/links-welcome";
import { WebAppRedirect } from "@components/web-app-redirect";

// constants
import { paths } from "@clockwise/client-commons/src/constants/site";
// util
import { isOnDraggableWorkingHours } from "@util/experiment.util";
import { getComponentOrRedirect, noopComponent, withRouteQuery } from "./routes.util";
import { Null } from "@clockwise/web-commons/src/components/null";
import {
  requireNoAuthApp,
  requireLogout,
  requireApp,
  requireSudo,
  requireInvite,
  requireAppWelcomeAmbiguous,
} from "@util/auth.util";
import { ClockwiseHome } from "@components/clockwise-home/ClockwiseHome";

//////////////////
// ROUTE CONFIG
//////////////////
const subRootRoutes = (
  <>
    <Route Component={HomeWrapper}>
      <Route
        path={`${paths.redirect}`}
        getComponent={(props) => getComponentOrRedirect(WebAppRedirect, requireNoAuthApp, props)}
      />
      <Route
        path={`${paths.slackLanding}/:step?`}
        Component={withRouteQuery(WebAppSlack, webAppSlackQuery)}
      />
      <Route path={paths.uninstall} Component={withRouteQuery(HomeUninstall, homeUninstallQuery)} />
      <Route
        path={`${paths.nonUserDefragSurvey}/:emailInstanceId`}
        Component={NonUserDefragSurveyPage}
      />
      <Route path={`${paths.unsubscribe}/:email/:token`} Component={UnsubscribeWrapperPage} />
      <Route
        path={`${paths.login}/:redirect?`}
        getComponent={(props) => getComponentOrRedirect(LoginWrapperPage, requireNoAuthApp, props)}
      />
      <Route
        path={`${paths.signup}/:redirect?`}
        getComponent={(props) => getComponentOrRedirect(LoginWrapperPage, requireNoAuthApp, props)}
        render={({ Component, props }) => {
          return Component ? <Component {...props} variant="invite" /> : null;
        }}
      />
      <Route
        path={`${paths.welcomeSignIn}/:redirect?`}
        getComponent={(props) => getComponentOrRedirect(LoginWrapperPage, requireNoAuthApp, props)}
        render={({ Component, props }) => {
          return Component ? <Component {...props} variant="welcome-ambiguous" /> : null;
        }}
      />
      <Route
        path={`${paths.welcome}/:redirect?`}
        getComponent={(props) => getComponentOrRedirect(LoginWrapperPage, requireNoAuthApp, props)}
        render={({ Component, props }) => {
          return Component ? <Component {...props} variant="welcome" /> : null;
        }}
      />
      <Route
        path={`${paths.linksOnboardingLogin}`}
        getComponent={(props) => getComponentOrRedirect(LinksWelcome, requireNoAuthApp, props)}
      />
    </Route>
    <Route
      path={paths.webApp}
      getComponent={(props) =>
        getComponentOrRedirect(WebAppCalendarRendererPage, requireApp, props)
      }
    >
      <Route Component={Null} />
      <Route Component={Null} path="/settings/:section?" />
      <Route Component={Null} path="/calendar" />
      <Route Component={Null} path="/team-analytics" />
      <Route Component={Null} path="/autopilot-list" />
      <Route Component={Null} path="/calendar-profile/:calendarId/:viewWeek?" />
      <Route Component={Null} path="/event/:encodedExternalEventId/:encodedCalendarId/:fullView?" />
      <Route Component={Null} path="/event/:encodedExternalEventId/:fullView?" />
    </Route>
    <Route
      path={paths.webApp}
      getComponent={(props) => getComponentOrRedirect(WebAppWrapperPage, requireApp, props)}
    >
      <Route path="/wat" Component={() => <div>wat</div>} />
      <Route
        path={"/teams"}
        getComponent={(props) => getComponentOrRedirect(WebAppTeamsPage, requireApp, props)}
      />
      <Route
        path={"/onboarding"}
        getComponent={(props) => getComponentOrRedirect(WebAppPage, requireApp, props)}
      />
      <Route
        path={"/settings/:section?/:subsection?/:subsubsection?"}
        getComponent={(props) => getComponentOrRedirect(WebAppPage, requireApp, props)}
      />
      <Route
        path={"/settings/team/primary/:feature?"}
        getComponent={(props) => getComponentOrRedirect(WebAppPage, requireApp, props)}
      />
      {isOnDraggableWorkingHours() ? (
        <Route
          path={paths.workingHours}
          getComponent={(props) =>
            getComponentOrRedirect(WebAppWorkingHoursPage, requireApp, props)
          }
        />
      ) : null}

      <Route
        path={`${paths.pricing}/:teamId?`}
        getComponent={(props) => getComponentOrRedirect(WebAppPricingPage, requireApp, props)}
      />

      <Route
        path={`${paths.checkout}/:teamId?`}
        getComponent={(props) => getComponentOrRedirect(WebAppCheckoutPage, requireApp, props)}
      />

      <Route
        path={`${paths.webSettings}/:path1?/:path2?/:path3?/:path4?`}
        getComponent={(props) => getComponentOrRedirect(WebSettings, requireApp, props)}
      />
    </Route>
    <Route
      path={paths.logout}
      getComponent={(props) => getComponentOrRedirect(noopComponent, requireLogout, props)}
    />
    <Route
      path={paths.signout}
      getComponent={(props) => getComponentOrRedirect(noopComponent, requireLogout, props)}
    />
    <Route
      path={paths.sudo}
      getComponent={(props) => getComponentOrRedirect(SudoWrapperPage, requireSudo, props)}
    />

    <Route
      path={`${paths.invite}/:inviteToken?`}
      getComponent={(props) => getComponentOrRedirect(noopComponent, requireInvite, props)}
    />

    <Route path={`${paths.chromeZoomVerifier}/account`} Component={ChromeZoomVerifierPage} />

    <Route path={`${paths.chromeZoomVerifier}/user`} Component={ChromeZoomVerifierPage} />

    <Route path={paths.chromeSlackVerifier} Component={ChromeSlackWrapperPage} />

    <Route path={`${paths.chromeSlackVerifier}/team/:teamId`} Component={ChromeSlackWrapperPage} />

    <Route path={paths.microsoftAuthRedirect} Component={MicrosoftAuthRedirect} />

    <Route
      path={`${paths.eventReschedule}/:encodedExternalEventId/:encodedCalendarId`}
      getComponent={(props) =>
        getComponentOrRedirect(ChromeEventRescheduleWrapperPage, requireApp, props)
      }
    />

    <Route
      path={"app/one-on-ones"}
      getComponent={(props) =>
        getComponentOrRedirect(ClockwiseHome("one-on-ones"), requireApp, props)
      }
    />
    <Route
      path={"app/scheduling-links"}
      getComponent={(props) =>
        getComponentOrRedirect(ClockwiseHome("scheduling-links"), requireApp, props)
      }
    />

    <Route
      path={`${paths.admin}/:view?/:billingGroupId?`}
      getComponent={(props) => getComponentOrRedirect(AdminPanelPage, requireApp, props)}
    />

    {/* Public Scheduling Link routes */}
    <Route path={paths.schedulingLink} Component={SchedulingLinkRenderer} />
    <Route path={paths.cancelBooking} Component={CancelBookingRenderer} />
    <Route path={paths.rescheduleBooking} Component={RescheduleBookingRenderer} />

    {/* Authenticated Scheduling Link routes */}
    <Route
      path={paths.createSchedulingLink}
      getComponent={(props) =>
        getComponentOrRedirect(SetupSchedulingLinkRenderer, requireApp, props)
      }
    />
    <Route
      path={paths.editSchedulingLink}
      getComponent={(props) =>
        getComponentOrRedirect(SetupSchedulingLinkRenderer, requireApp, props)
      }
    />

    <Route path={paths.asana} Component={withRouteQuery(AsanaWrapper, asanaWrapperQuery)}>
      <Route
        getComponent={(props) =>
          getComponentOrRedirect(AsanaAuth, requireAppWelcomeAmbiguous, props)
        }
      />
      <Route
        path={`task/:calendar_id/:event_id`}
        getComponent={(props) =>
          getComponentOrRedirect(AsanaTask, requireAppWelcomeAmbiguous, props)
        }
      />
    </Route>
  </>
);

export const routeConfig = makeRouteConfig(
  <>
    <Route path="/" Component={ModernWrapperPage}>
      {subRootRoutes}
    </Route>
    <Route path="/test-:mainBranch" Component={ModernWrapperPage}>
      {subRootRoutes}
    </Route>
  </>,
);
